<template>
  <div class="gs-app gs-aff-page gs-affiliate-promos">
    <header class="gs-aff-page__head"><div><h1>My promotions</h1><p>Find your affiliate links, promotion tools, and bonuses for each program.</p></div></header>
    <v-wait for="promos">
      <div class="gs-aff-toolbar gs-refresh-controls">
        <label class="gs-aff-toolbar__search">{{ $t('frontend_search') }}<input v-model="search" type="search" placeholder="Search promotions" /></label>
        <label>Sort by<gs-name-select v-model="selectedOrder" :options="sortOrderOptions" placeholder="Sort promotions" /></label>
        <label>Status<gd-dropdown v-model="filterOption" :options="filterOptions" /></label>
      </div>
      <div v-if="filteredPromos.length" class="gs-aff-surface">
        <div class="gs-aff-table-wrap">
          <table class="gs-aff-table">
            <caption>{{ filteredPromos.length }} {{ filteredPromos.length === 1 ? 'promotion' : 'promotions' }}</caption>
            <thead><tr><th v-for="column in sortColumns" :key="column.value" :aria-sort="sortDirection(column.value)"><button type="button" class="gs-aff-table__sort" @click="sortColumn(column.value)">{{ column.title }}<span v-if="sortDirection(column.value) !== 'none'" aria-hidden="true">{{ sortDirection(column.value) === 'ascending' ? '↑' : '↓' }}</span></button></th><th>Actions</th></tr></thead>
            <tbody><tr v-for="promo in filteredPromos" :key="promo.promo_id || promo.id">
              <td class="gs-aff-table__name" data-label="Promotion">{{ promo.name }}</td>
              <td data-label="Impressions">{{ promo.impressions }}</td><td data-label="Unique visits">{{ promo.uniques }}</td>
              <td data-label="Opt-ins">{{ promo.optins }}</td><td data-label="Conversions">{{ promo.conversions }}</td>
              <td data-label="Commissions">{{ promo.currency_symbol }}{{ promo.commissions }}</td>
              <td data-label="Actions"><div class="gs-aff-table__actions">
                <gs-button size="sm" variant="outline" @click="fetchAffiliateLinks(promo, promo.id, promo.main_product_id)"><gs-icon name="link" size="16" />Links</gs-button>
                <gs-button size="sm" variant="outline" @click="fetchAffiliateBonuses(promo.id)">Bonuses</gs-button>
                <gs-button size="sm" variant="ghost" @click="dashboard(promo.id)">Promo tools</gs-button>
                <gs-button size="sm" variant="ghost" :disabled="!promo.has_data_sharing" @click="dataSharing(promo.id)">Data sharing</gs-button>
                <gs-button v-if="filterOption === 'active'" size="sm" variant="ghost" @click="archiveData(promo.promo_id)">Archive</gs-button>
              </div></td>
            </tr></tbody>
          </table>
        </div>
      </div>
      <p v-else class="gs-aff-empty">{{ search || filterOption === 'archive' ? 'No promotions match these filters.' : $t('frontend_affiliate_promos_you_havent') }}</p>
      <affiliate-links-modal :affiliateLinks="affiliateLinks" :openCreate="openCreate" :openEditCustomLandingPage="openEditCustomLandingPage" :deleteLandingPageUrl="openConfirmModal" :selectedFunnelId="selectedFunnelId" />
      <affiliate-bonuses-modal :affiliateBonuses="affiliateBonuses" :bonuses="bonuses" :affiliateBonusesTab="affiliateBonusesTab" @affiliate-bonuses-tab-change="handleAffiliateBonusTabChange" />
      <add-custom-landing-page-url :addLandingPageUrl="addLandingPageUrl" :model="model" :validator="$v" @update-model="HandleUpdateModel" />
      <edit-custom-landing-page-url :model="model" :validator="$v" :editCustomLandingPage="editCustomLandingPage" @update-model="HandleUpdateModel" />
    </v-wait>
    <confirm-modal name="confirm-landing-page-url-delete-modal" :message-content="$t('frontend_delete_custom_landing_page_text')" :button-text="$t('frontend_delete_custom_landing_page_confirmation_text')" button-size="auto" @confirm="deleteLandingPageUrl" />
  </div>
</template>
<script>
import ConfirmModal from "./components/AffiliateConfirmModal.vue";
import Promo from '@/models/Promo';
import AffiliateLink from '@/models/AffiliateLink';
import AffiliateBonus from '@/models/AffiliateBonus';
import Bonus from '@/models/Bonus';
import { required, minLength, maxLength, url } from 'vuelidate/lib/validators';
import './affiliate-workspace.css';
import axios from 'axios';

/** new modals */
import AffiliateLinksModal from './Promo_partials/modals/AffiliateLinksModal';
import AffiliateBonusesModal from './Promo_partials/modals/AffiliateBonusesModal';
import AddCustomLandingPageUrl from './Promo_partials/modals/AddCustomLandingPageUrlModal';
import EditCustomLandingPageUrl from './Promo_partials/modals/EditCustomLandingPageUrlModal';


export default {
  middleware: 'auth',
  layout: 'defaultForAffiliates',
  metaInfo() {
    return { title: 'My Affiliate Promotions' };
  },
  components: {
    ConfirmModal,
    AffiliateLinksModal,
    AffiliateBonusesModal,
    AddCustomLandingPageUrl,
    EditCustomLandingPageUrl,
  },
  data() {
    return {
      landingPageUrlId: null,
      model: {
        name: '',
        type: 2,
        affiliate_id: '',
        funnel_id: '',
        product_id: '',
        landing_page_id: 0,
        link: '',
      },
      custom_landing_page: null,
      search: '',
      filterOption: 'active',
      promos: [],
      affiliateLinks: [],
      affiliateBonuses: [],
      affiliateBonusesTab: '',
      bonuses: [],
      selectedFunnelId: 0,
      selectedMainProductId: 0,
      selectedOrder: 'created_at',
    };
  },

  validations: {
    model: {
      name: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(80),
      },
      link: {
        required,
        url,
      },
    },
  },

  computed: {
    sortColumns() {
      return [
        { value: 'name', title: 'Promotion' },
        { value: 'impressions', title: 'Impressions' },
        { value: 'uniques', title: 'Unique visits' },
        { value: 'optins', title: 'Opt-ins' },
        { value: 'conversions', title: 'Conversions' },
        { value: 'commissions', title: 'Commissions' },
      ];
    },
    sortOrderOptions() {
      return [
        { value: "created_at", text: this.$t('frontend_groovesell_created_oldest_to_newest') },
        { value: "-created_at", text: this.$t('frontend_groovesell_created_newest_to_oldest') },
        { value: "updated_at", text: this.$t('frontend_groovesell_updated_oldest_to_newest') },
        { value: "-updated_at", text: this.$t('frontend_groovesell_updated_newest_to_oldest') },
        { value: "name", text: `${this.$t('frontend_name')} (A-Z)` },
        { value: "-name", text: `${this.$t('frontend_name')} (Z-A)` }
      ].concat(this.sortColumns.slice(1).reduce((options, column) => options.concat([
        { value: column.value, text: `${column.title} (Low to High)` },
        { value: `-${column.value}`, text: `${column.title} (High to Low)` },
      ]), []));
    },
    filterOptions() {
      return [
        { value: "active", text: this.$t('frontend_active') },
        { value: "archive", text: this.$t('frontend_archive') }
      ];
    },
    filteredPromos() {
      const active = this.filterOption !== 'archive';
      const query = this.search.trim().toLowerCase();
      const sortField = this.selectedOrder.replace(/^-/, '');
      const direction = this.selectedOrder.startsWith('-') ? -1 : 1;
      return this.promos.filter(promo => (active ? Number(promo.archived_data) === 0 : Number(promo.archived_data) === 1)
        && String(promo.name || '').toLowerCase().includes(query)).slice().sort((a, b) => {
          const numeric = ['impressions', 'uniques', 'optins', 'conversions', 'commissions'].includes(sortField);
          const left = numeric ? Number(String(a[sortField] || 0).replace(/,/g, '')) : a[sortField] || '';
          const right = numeric ? Number(String(b[sortField] || 0).replace(/,/g, '')) : b[sortField] || '';
          return direction * (left === right ? 0 : left > right ? 1 : -1);
        });
    },

    customLandingPageLinks() {
      return this.affiliateLinks.filter((link) => {
        return link.type === 'custom_land_page';
      });
    },


  },

  async created() {
    this.$wait.start('promos');
    this.promos = await Promo.$get();
    this.bonuses = await Bonus.$get();
    this.$wait.end('promos');
  },

  methods: {
    sortColumn(field) {
      this.selectedOrder = this.selectedOrder === field ? `-${field}` : field;
    },
    sortDirection(field) {
      if (this.selectedOrder.replace(/^-/, '') !== field) return 'none';
      return this.selectedOrder.startsWith('-') ? 'descending' : 'ascending';
    },
    handleAffiliateBonusTabChange(newTab) {
      this.affiliateBonusesTab = newTab;
    },
    HandleUpdateModel(newModelValue) {
      this.model = newModelValue;
    },
    async archiveData(id) {
      try {
        this.$loader.start('archiveData');
        await axios.post(`/promos/${id}/archive`);

        const index = this.promos.findIndex(promo => promo.promo_id === id);

        if (index !== -1) {
          this.promos[index].archived_data = 1;
        }
        this.$loader.end('archiveData');

      } catch (error) {
        this.$loader.end('archiveData');
        if (error.response && error.response.status === 404) {
          console.error('Data not found');
        } else {
          console.error(error);
        }
      }
    },
    openCreate() {
      this.$v.$reset();
      this.custom_landing_page = new AffiliateLink({});
      this.model.name = '';
      this.model.link = '';
      this.$root.$emit('modal-open', 'add-custom-landing-page-url');
    },
    openConfirmModal(landingPageUrlId) {
      this.landingPageUrlId = landingPageUrlId;
      this.$root.$emit('modal-open', 'confirm-landing-page-url-delete-modal');
    },
    async deleteLandingPageUrl() {
      this.$loader.start('deleteLandingPageUrl');
      const affiliateLink = await AffiliateLink.$find(this.landingPageUrlId);
      await affiliateLink.delete();

      const deletedAffiliateLink2 = this.affiliateLinks
        .map((item) => item.id)
        .indexOf(this.landingPageUrlId);
      if (deletedAffiliateLink2 !== -1) this.affiliateLinks.splice(deletedAffiliateLink2, 1);
      this.$loader.end('deleteLandingPageUrl');
    },

    async addLandingPageUrl() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.$loader.start('addLandingPageUrl');
        this.custom_landing_page.name = this.model.name;
        this.custom_landing_page.type = this.model.type;
        this.custom_landing_page.funnel_id = this.selectedFunnelId;
        this.custom_landing_page.product_id = this.selectedMainProductId;
        this.custom_landing_page.landing_page_id = this.model.landing_page_id;
        this.custom_landing_page.link = this.model.link;

        const savedCustomLandingPage = await this.custom_landing_page.save();
        this.customLandingPageLink = savedCustomLandingPage.data;

        this.affiliateLinks.unshift(this.customLandingPageLink);
        this.$loader.end('addLandingPageUrl');
        this.$root.$emit('modal-close', 'add-custom-landing-page-url');
      }
    },

    async openEditCustomLandingPage(id) {
      this.$loader.start('openEditCustomLandingPage');
      this.custom_landing_page = await AffiliateLink.$find(id);
      this.model.name = this.custom_landing_page.name;
      this.model.link = this.custom_landing_page.landing_page_url;
      this.$loader.end('openEditCustomLandingPage');
      this.$root.$emit('modal-open', 'edit-custom-landing-page-url');
    },

    async editCustomLandingPage() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.$loader.start('editCustomLandingPage');
        this.custom_landing_page.name = this.model.name;
        this.custom_landing_page.link = this.model.link;
        const savedCustomLandingPage = await this.custom_landing_page.save();

        const index = this.affiliateLinks.findIndex(link => link.id === savedCustomLandingPage.data.id);
        if (index !== -1) this.affiliateLinks.splice(index, 1, savedCustomLandingPage.data);

        this.$loader.end('editCustomLandingPage');
        this.$root.$emit('modal-close', 'edit-custom-landing-page-url');
      }
    },

    async editBonus() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.$loader.start('editBonus');
        this.bonus.name = this.model.name;
        this.bonus.link = this.model.link;
        const savedBonus = await this.bonus.save();
        const editedBonus = this.bonuses
          .map((item) => item.id)
          .indexOf(savedBonus.data.id);
        this.bonuses[editedBonus] = savedBonus.data;
        this.$loader.end('editBonus');
        this.$root.$emit('modal-close', 'edit-bonus-modal');
      }
    },


    async fetchAffiliateLinks(row, id, mainProductId) {
      this.$loader.start('fetchAffiliateLinks');
      this.affiliateLinksTab = 'aff_links';
      this.affiliateLinks = await AffiliateLink.params({
        funnel_id: id,
      }).$get();
      this.selectedFunnelId = id;
      this.selectedMainProductId = mainProductId;
      this.$loader.end('fetchAffiliateLinks');
      this.$root.$emit('modal-open', 'affiliate-links-modal');
    },

    async fetchAffiliateBonuses(id) {
      this.$loader.start('fetchAffiliateBonuses');
      this.affiliateBonuses = await AffiliateBonus.params({
        funnel_id: id,
      }).$get();

      if (this.affiliateBonuses.length > 0) {
        this.affiliateBonusesTab = `affiliate_bonus_${this.affiliateBonuses[0].product_id}`;
      }

      this.$loader.end('fetchAffiliateBonuses');
      this.$root.$emit('modal-open', 'affiliate-bonuses-modal');
    },
    dashboard(id) {
      this.$router.push({ name: 'promos_dashboard', params: { id } });
    },
    dataSharing(id) {
      this.$router.push({ name: 'data_sharing', params: { funnel_id: id } });
    },
  },
};
</script>
